import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { Observable, Subscription } from 'rxjs';
import { CatalogDoaApproverFormComponent, DoaApproverFormValue } from '../catalog-doa-approver-form/catalog-doa-approver-form.component';
import { CatalogDoaRangeFormComponent, DoaRangeFormValue } from '../catalog-doa-range-form/catalog-doa-range-form.component';
import { CatalogFilterPanelComponent } from '../catalog-filter-panel/catalog-filter-panel.component';
import { CatalogPageHeaderComponent } from '../catalog-page-header/catalog-page-header.component';
import { CatalogSidePanelComponent } from '../catalog-side-panel/catalog-side-panel.component';
import { CatalogTableComponent } from '../catalog-table/catalog-table.component';
import {
  CatalogEntityConfig,
  CatalogQuickFilter,
  CatalogRow,
  CatalogThemeVariables,
  DomainCatalogConfig,
  DomainCatalogViewModel,
  getPendingCatalogChangeCount,
} from '@shared-lib/models/domain-catalog.models';
import { DomainCatalogDataService } from '@shared-lib/shared-services/domain-catalog-data.service';
import { ThemeService } from '@shared-lib/shared-services/theme.service';
import { PendingChangesService } from '@shared-lib/shared-services/pending-changes.service';

/** Catalog id of the Part Distribution Centres (PDC) entity. */
const PDC_CATALOG_ID = 'pricing-decision-comittee';

/** Hardcoded External Instance dropdown options for the PDC catalog's Add Row form. */
const PDC_EXTERNAL_INSTANCE_OPTIONS = [
  'MDCGOM',
  'SGPGOM',
  'RUMGOM',
  'EUAGOM',
  'NAGOM',
  'CHPGOM',
  'CHRGOM',
  'SLP1P',
  'CBZ1P',
];

@Component({
  selector: 'app-domain-catalogs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CatalogSidePanelComponent,
    CatalogPageHeaderComponent,
    CatalogDoaApproverFormComponent,
    CatalogDoaRangeFormComponent,
    CatalogFilterPanelComponent,
    CatalogTableComponent,
  ],
  templateUrl: './domain-catalogs.component.html',
  styleUrl: './domain-catalogs.component.scss',
  providers: [DomainCatalogDataService, ResponsiveLayoutService],
  encapsulation: ViewEncapsulation.None,
})
export class DomainCatalogsComponent implements OnInit, OnDestroy {
  @Input() data: any;
  readonly vm$: Observable<DomainCatalogViewModel>;
  private readonly subs = new Subscription();
  themeMode: 'light' | 'dark' = 'light';
  private catalogThemeOverrides: CatalogThemeVariables = {};
  private unregisterPendingChanges?: () => void;
  doaRangeEditRowId: string | null = null;
  doaRangeEditValue: DoaRangeFormValue | null = null;
  doaApproverEditRowId: string | null = null;
  doaApproverEditValue: DoaApproverFormValue | null = null;
  @ViewChild('deleteCancelBtn') private deleteCancelBtn?: ElementRef<HTMLButtonElement>;
  private deleteConfirmWasOpen = false;

  constructor(
    public readonly catalogs: DomainCatalogDataService,
    private readonly responsiveLayoutService: ResponsiveLayoutService,
    private readonly themeService: ThemeService,
    private readonly pendingChangesService: PendingChangesService,
  ) {
    this.vm$ = this.catalogs.vm$;
  }

  ngOnInit(): void {
    this.responsiveLayoutService.init();
    this.unregisterPendingChanges = this.pendingChangesService.register(() =>
      this.catalogs.hasPendingChanges(),
    );
    const configUrl = this.data?.configUrl || this.data?.configurationUrl || this.data?.apiUrl || 'assets/dashboard-config/domain-catalogs.json';
    const dataUrl = this.data?.dataApiUrl || (this.data?.configUrl || this.data?.configurationUrl ? this.data?.apiUrl : undefined);
    this.catalogs.load(configUrl, dataUrl);
    this.updateResponsiveState();

    this.subs.add(
      this.responsiveLayoutService.reflow$.subscribe(() => this.updateResponsiveState()),
    );
    this.subs.add(
      this.themeService.theme$.subscribe((theme) => {
        this.themeMode = this.isDarkTheme(theme) ? 'dark' : 'light';
        this.catalogThemeOverrides = this.buildCatalogThemeOverrides(theme);
      }),
    );

    // Move focus onto the Cancel button as soon as the delete confirmation
    // popup opens, so keyboard users land somewhere sensible inside it.
    this.subs.add(
      this.vm$.subscribe((vm) => {
        if (vm.showDeleteConfirmModal && !this.deleteConfirmWasOpen) {
          setTimeout(() => this.deleteCancelBtn?.nativeElement.focus());
        }
        this.deleteConfirmWasOpen = vm.showDeleteConfirmModal;
      }),
    );
  }

  ngOnDestroy(): void {
    this.unregisterPendingChanges?.();
    this.subs.unsubscribe();
    this.responsiveLayoutService.destroy();
  }

  /** Warn before a refresh/close discards unsaved local changes on any catalog. */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.catalogs.hasPendingChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** Escape closes the delete confirmation popup, matching standard dialog behavior. */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.catalogs.cancelDeleteConfirm();
  }

  /**
   * Refreshes the active catalog only after confirming that its unsaved work
   * may be discarded. The browser's beforeunload warning does not run for this
   * in-app refresh button, so it needs the equivalent guard explicitly.
   */
  refreshCatalog(): void {
    if (
      this.catalogs.hasActivePendingChanges() &&
      !window.confirm(
        'You have pending changes on this catalog. Refreshing will discard them. Do you want to continue?',
      )
    ) {
      return;
    }

    this.cancelDoaEdit();
    this.catalogs.refresh();
  }

  /** `ui.hoverSelection` from the widget config; hover-to-select stays on when omitted. */
  get hoverSelection(): boolean {
    const flag = this.data?.ui?.hoverSelection;
    return typeof flag === 'boolean' ? flag : true;
  }

  private updateResponsiveState(): void {
    this.catalogs.setCompact(window.innerWidth < 960);
  }

  getThemeStyles(config: DomainCatalogConfig | null): CatalogThemeVariables {
    return {
      ...this.getCatalogConfigThemeStyles(config),
      ...this.catalogThemeOverrides,
    };
  }

  /**
   * Number of unsaved changes to surface on the Save button. Mirrors what
   * `saveAll` actually persists. Each affected row is counted once, including
   * edits, inserts, active toggles, and deletes.
   */
  getPendingChangeCount(vm: DomainCatalogViewModel): number {
    return getPendingCatalogChangeCount(vm);
  }

  /** Entity whose `tabs` include the active catalog (e.g. DOA for its two tab views). */
  getTabParent(vm: DomainCatalogViewModel): CatalogEntityConfig | null {
    const entities = vm.config?.entities || [];
    return entities.find((entity) => entity.tabs?.some((tab) => tab.id === vm.activeCatalogId)) || null;
  }

  /** Sidebar highlight id: a tab view highlights its parent's card. */
  getNavActiveId(vm: DomainCatalogViewModel): string {
    return this.getTabParent(vm)?.id || vm.activeCatalogId;
  }

  /** The DOA Range Level tab swaps the refine panel for the range entry form. */
  showDoaRangeForm(vm: DomainCatalogViewModel): boolean {
    return vm.activeCatalogId === 'doa-range-level';
  }

  /** BU options for the DOA range form: API options, else values already in the table. */
  getDoaBusinessUnits(vm: DomainCatalogViewModel): string[] {
    if (this.catalogs.businessUnitOptions.length) return this.catalogs.businessUnitOptions;
    return this.distinctRowValues(vm, 'businessSegment');
  }

  /** Stages the form value as a pending insert; the table's Save button persists it. */
  saveDoaRange(value: DoaRangeFormValue): void {
    const rowValues = {
      businessSegment: value.businessUnit,
      rangeLevel: value.rangeLevel,
      ranges: `${value.range1.trim()} / ${value.range2.trim()}`,
    };
    if (this.doaRangeEditRowId) {
      this.catalogs.updateDoaRow(this.doaRangeEditRowId, rowValues);
      this.doaRangeEditRowId = null;
      this.doaRangeEditValue = null;
    } else {
      this.catalogs.addStagedRow(rowValues);
    }
  }

  /** The DOA Approver Level tab swaps the refine panel for the approver form. */
  showDoaApproverForm(vm: DomainCatalogViewModel): boolean {
    return vm.activeCatalogId === 'doa-approver-level';
  }

  /** Region options for the DOA approver form: API options, else values already in the table. */
  getDoaRegions(vm: DomainCatalogViewModel): string[] {
    if (this.catalogs.regionOptions.length) return this.catalogs.regionOptions;
    return this.distinctRowValues(vm, 'region');
  }

  /**
   * DOA tables hide the inline Add Row button (rows are entered through the DOA
   * forms) but keep the shared Save button, pending-changes note, and Discard
   * Changes action. `inlineEditing: false` keeps rows read-only: DOA edits are
   * staged from the upper form and shown as queued values, not inline inputs.
   */
  getTableUiConfig(vm: DomainCatalogViewModel): { showAddButton: boolean; showSaveAllButton: boolean; inlineEditing: boolean } | null {
    return this.showDoaRangeForm(vm) || this.showDoaApproverForm(vm)
      ? DomainCatalogsComponent.DOA_TABLE_UI
      : null;
  }

  private static readonly DOA_TABLE_UI = { showAddButton: false, showSaveAllButton: true, inlineEditing: false };

  /** Quick filters rendered inside the two DOA table panels. */
  getTableQuickFilters(vm: DomainCatalogViewModel): CatalogQuickFilter[] {
    if (this.showDoaRangeForm(vm)) return this.getDoaRangeFilters(vm);
    if (this.showDoaApproverForm(vm)) return this.getDoaApproverFilters(vm);
    return DomainCatalogsComponent.NO_QUICK_FILTERS;
  }

  private static readonly NO_QUICK_FILTERS: CatalogQuickFilter[] = [];

  getDoaRangeFilters(vm: DomainCatalogViewModel): CatalogQuickFilter[] {
    this.syncDoaOptionsCache(vm);
    if (!this.doaRangeFiltersCache) {
      this.doaRangeFiltersCache = [
        { key: 'businessSegment', label: 'BU', options: this.distinctRowValues(vm, 'businessSegment') },
        { key: 'rangeLevel', label: 'Range Level', options: this.distinctRowValues(vm, 'rangeLevel') },
      ];
    }
    return this.doaRangeFiltersCache;
  }

  /** Filter dropdowns for the DOA approver table; options come from the loaded rows. */
  getDoaApproverFilters(vm: DomainCatalogViewModel): CatalogQuickFilter[] {
    this.syncDoaOptionsCache(vm);
    if (!this.doaApproverFiltersCache) {
      this.doaApproverFiltersCache = [
        { key: 'businessSegment', label: 'BU', options: this.distinctRowValues(vm, 'businessSegment') },
        { key: 'region', label: 'Region', options: this.distinctRowValues(vm, 'region') },
        { key: 'approvalType', label: 'Function', options: this.distinctRowValues(vm, 'approvalType') },
        { key: 'approver', label: 'Approver', options: this.distinctRowValues(vm, 'approver') },
        { key: 'rangeLevel', label: 'Range Level', options: this.distinctRowValues(vm, 'rangeLevel') },
        { key: 'serialOrder', label: 'Serial Order', options: this.distinctRowValues(vm, 'serialOrder') },
      ];
    }
    return this.doaApproverFiltersCache;
  }

  saveDoaApprover(value: DoaApproverFormValue): void {
    const rowValues = {
      businessSegment: value.businessUnit,
      region: value.region,
      rangeLevel: value.rangeLevel,
      approver: value.approver,
      serialOrder: value.serialOrder,
      approvalType: value.approvalType,
      active: value.active,
      status: value.active ? 'Active' : 'Inactive',
    };
    if (this.doaApproverEditRowId) {
      this.catalogs.updateDoaRow(this.doaApproverEditRowId, rowValues);
      this.doaApproverEditRowId = null;
      this.doaApproverEditValue = null;
    } else {
      this.catalogs.addDoaApproverRow(rowValues);
    }
  }

  /** DOA edit actions populate the upper form; all other catalogs retain inline editing. */
  editRow(vm: DomainCatalogViewModel, row: CatalogRow): void {
    if (this.showDoaRangeForm(vm)) {
      const [range1 = '', range2 = ''] = String(row['ranges'] || '').split(/\s*\/\s*/, 2);
      this.doaRangeEditRowId = row.id;
      this.doaRangeEditValue = {
        businessUnit: String(row['businessSegment'] || ''),
        rangeType: 'existing',
        rangeLevel: Number(row['rangeLevel']) || 1,
        range1,
        range2,
      };
      return;
    }
    if (this.showDoaApproverForm(vm)) {
      this.doaApproverEditRowId = row.id;
      this.doaApproverEditValue = {
        businessUnit: String(row['businessSegment'] || ''),
        region: String(row['region'] || ''),
        rangeLevel: Number(row['rangeLevel']) || 1,
        active: row.active !== false,
        approver: String(row['approver'] || ''),
        serialOrder: Number(row['serialOrder']) || 1,
        approvalType: String(row['approvalType'] || 'Finance'),
      };
      return;
    }
    this.catalogs.toggleEdit(row);
  }

  /** Reset button on either DOA form: clears any in-progress edit so the form returns to add-new mode. */
  cancelDoaEdit(): void {
    this.doaRangeEditRowId = null;
    this.doaRangeEditValue = null;
    this.doaApproverEditRowId = null;
    this.doaApproverEditValue = null;
  }

  selectCatalog(catalogId: string): void {
    this.doaRangeEditRowId = null;
    this.doaRangeEditValue = null;
    this.doaApproverEditRowId = null;
    this.doaApproverEditValue = null;
    this.catalogs.selectCatalog(catalogId);
  }

  /**
   * These getters run from the template on every change-detection cycle, so
   * the derived option lists are cached against the `vm.rows` reference:
   * returning fresh arrays/objects each cycle forced the dropdowns' *ngFor to
   * rebuild their DOM continuously, freezing the page.
   */
  private doaOptionsCacheRows: DomainCatalogViewModel['rows'] | null = null;
  private doaDistinctCache = new Map<string, string[]>();
  private doaRangeFiltersCache: CatalogQuickFilter[] | null = null;
  private doaApproverFiltersCache: CatalogQuickFilter[] | null = null;
  private readonly doaCollator = new Intl.Collator(undefined, { numeric: true });

  private syncDoaOptionsCache(vm: DomainCatalogViewModel): void {
    if (vm.rows === this.doaOptionsCacheRows) return;
    this.doaOptionsCacheRows = vm.rows;
    this.doaDistinctCache.clear();
    this.doaRangeFiltersCache = null;
    this.doaApproverFiltersCache = null;
  }

  private distinctRowValues(vm: DomainCatalogViewModel, key: string): string[] {
    this.syncDoaOptionsCache(vm);
    const cached = this.doaDistinctCache.get(key);
    if (cached) return cached;

    const values = vm.rows
      .map((row) => String(row[key] ?? '').trim())
      .filter(Boolean);
    const options = Array.from(new Set(values)).sort(this.doaCollator.compare);
    this.doaDistinctCache.set(key, options);
    return options;
  }

  getDraftSelectOptions(activeCatalogId: string): Record<string, string[]> {
    const options: Record<string, string[]> = {};

    if (activeCatalogId !== 'business-segments') {
      const businessUnitOptions = this.catalogs.businessUnitOptions;
      if (businessUnitOptions.length) {
        options['businessSegment'] = businessUnitOptions;
      }
    }

    if (activeCatalogId !== 'region') {
      const regionOptions = this.catalogs.regionOptions;
      if (regionOptions.length) {
        options['region'] = regionOptions;
      }
    }

    if (activeCatalogId !== 'country') {
      const countryOptions = this.catalogs.countryOptions;
      if (countryOptions.length) {
        options['country'] = countryOptions;
      }
    }

    // The PDC catalog's External Instance is a fixed set of codes, so it renders
    // as a dropdown (Add Row / inline edit) rather than a free-text input.
    if (activeCatalogId === PDC_CATALOG_ID) {
      options['external_instance'] = PDC_EXTERNAL_INSTANCE_OPTIONS;
    }

    return options;
  }

  private getCatalogConfigThemeStyles(config: DomainCatalogConfig | null): CatalogThemeVariables {
    const styles = { ...(config?.theme?.[this.themeMode] || {}) };
    delete styles['--primary'];
    delete styles['--primary-end'];
    delete styles['--primary-rgb'];
    delete styles['--primary-bg'];
    return styles;
  }

  private buildCatalogThemeOverrides(theme: any): CatalogThemeVariables {
    if (!theme) return {};
    const primary = theme.button?.primaryBg || theme.layout?.sidebar?.bg || '#043D63';
    const primaryEnd = theme.button?.primaryHoverBg || primary;
    const primaryRgb = this.hexToRgb(primary) || '4, 61, 99';

    return {
      '--primary': primary,
      '--primary-end': primaryEnd,
      '--primary-rgb': primaryRgb,
      '--primary-bg': `rgba(${primaryRgb}, ${this.themeMode === 'dark' ? '0.16' : '0.08'})`,
      '--bg-app': theme.layout?.content?.bg || 'var(--dvm-content-bg, #d9dbdd)',
      '--text-main': theme.layout?.content?.text || 'var(--dvm-content-text, #202124)',
      '--text-muted': theme.chart?.axisLabelColor || 'var(--dvm-form-placeholder-color, #5f6368)',
    };
  }

  private hexToRgb(hex: string): string | null {
    const normalized = hex.replace('#', '').trim();
    if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }

  private isDarkTheme(theme: any): boolean {
    const bg = String(theme?.layout?.content?.bg || '').replace('#', '').slice(0, 6);
    if (!/^[0-9a-f]{6}$/i.test(bg)) return this.themeService.currentThemeName === 'dark';
    const r = parseInt(bg.slice(0, 2), 16);
    const g = parseInt(bg.slice(2, 4), 16);
    const b = parseInt(bg.slice(4, 6), 16);
    return r * 0.299 + g * 0.587 + b * 0.114 < 128;
  }
}


